import { splitRegion } from "@/lib/region"
import { ROUTES } from "@/lib/routes"
import { redactAuthUrl } from "@/platform/api/redact-auth-url"
import { resolveNavigationRule } from "@/platform/app-mode/navigation-rules"
import type { BarButtonData, BarButtonIcon, PageMetaData } from "@/platform/bridge/types"

export interface BarButtonSpec {
  readonly id: string
  readonly icon: BarButtonIcon
  readonly labelKey: "openSearch" | "bag"
  readonly path: string
}

const BAR_BUTTONS: ReadonlyArray<BarButtonSpec> = [
  { id: "search", icon: "search", labelKey: "openSearch", path: `/${ROUTES.SEARCH}` },
  { id: "cart", icon: "cart", labelKey: "bag", path: `/${ROUTES.CART}` },
]

/** The locale prefix plus the region segment the URL carries; "" for the default region at the root. */
const regionBase = (pathname: string): string => {
  const { prefix, region, explicit } = splitRegion(pathname)

  return explicit ? `${prefix}/${region}` : prefix
}

/** The storefront home for the page's locale and region, e.g. "/", "/en" or "/us". */
export const regionRoot = (pathname: string): string => regionBase(pathname) || "/"

export const barTargetPath = (pathname: string, id: string): string | null => {
  const spec = BAR_BUTTONS.find((button) => button.id === id)

  return spec === undefined ? null : `${regionBase(pathname)}${spec.path}`
}

export const barButtonSpecs = (pathname: string): ReadonlyArray<BarButtonSpec> => {
  const rule = resolveNavigationRule(pathname)
  if (rule === undefined || rule.header === "none" || rule.screen === "native" || rule.screen === "external") return []

  return BAR_BUTTONS.filter((button) => barTargetPath(pathname, button.id) !== pathname)
}

export interface HeadingNode {
  readonly textContent: string | null
  readonly childNodes: ArrayLike<{ readonly textContent: string | null }>
}

export interface HeadingSource {
  querySelector(selector: "h1"): HeadingNode | null
}

const headingText = (heading: HeadingNode): string =>
  heading.childNodes.length === 0 ? (heading.textContent ?? "") : Array.from(heading.childNodes, (node) => node.textContent ?? "").join(" ")

export const screenTitleFor = (pathname: string, root: HeadingSource): string => {
  const rule = resolveNavigationRule(pathname)
  if (rule === undefined || rule.screen !== "push" || rule.header !== "standard") return ""

  const heading = root.querySelector("h1")
  return heading === null ? "" : headingText(heading).replace(/\s+/g, " ").trim()
}

export const pageMetaFor = ({ url, title, scrollY, bar }: { url: string; title: string; scrollY: number; bar: ReadonlyArray<BarButtonData> }): PageMetaData => ({
  url: redactAuthUrl(url),
  ...(title === "" ? {} : { title }),
  scrollY: Math.round(scrollY),
  ...(bar.length === 0 ? {} : { bar }),
})
